/**
 * Date Formatter Composable
 * Handles all date/time formatting with consistent logic
 */

export const useDateFormatter = () => {
  /**
   * Convert various timestamp formats to Date object
   * @param {*} timestamp - Firestore Timestamp, Date, number, or string
   * @returns {Date|null}
   */
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return null;

    try {
      // Firestore Timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      // Already a Date
      if (timestamp instanceof Date) {
        return timestamp;
      }
      // Unix timestamp (number)
      if (typeof timestamp === 'number') {
        return new Date(timestamp);
      }
      // String date
      if (typeof timestamp === 'string') {
        return new Date(timestamp);
      }
      return null;
    } catch (error) {
      console.error('Error parsing timestamp:', error);
      return null;
    }
  };

  /**
   * Format date and time together
   * @param {*} timestamp
   * @param {string} locale - default 'th-TH'
   * @returns {string}
   */
  const formatDateTime = (timestamp, locale = 'th-TH') => {
    const date = parseTimestamp(timestamp);
    if (!date || isNaN(date.getTime())) return 'N/A';

    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  /**
   * Format date only
   * @param {*} timestamp
   * @param {string} locale - default 'th-TH'
   * @returns {string}
   */
  const formatDate = (timestamp, locale = 'th-TH') => {
    const date = parseTimestamp(timestamp);
    if (!date || isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Format time only
   * @param {*} timestamp
   * @param {string} locale - default 'th-TH'
   * @returns {string}
   */
  const formatTime = (timestamp, locale = 'th-TH') => {
    const date = parseTimestamp(timestamp);
    if (!date || isNaN(date.getTime())) return '';

    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  /**
   * Format date for filter display
   * @param {string} dateStr - ISO date string
   * @param {string} locale - default 'th-TH'
   * @returns {string}
   */
  const formatFilterDate = (dateStr, locale = 'th-TH') => {
    if (!dateStr) return '';

    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Error formatting filter date:', error);
      return dateStr;
    }
  };

  /**
   * Check if two dates are the same day
   * @param {*} timestamp1
   * @param {*} timestamp2
   * @returns {boolean}
   */
  const isSameDay = (timestamp1, timestamp2) => {
    const date1 = parseTimestamp(timestamp1);
    const date2 = parseTimestamp(timestamp2);

    if (!date1 || !date2) return false;

    return date1.toDateString() === date2.toDateString();
  };

  return {
    parseTimestamp,
    formatDateTime,
    formatDate,
    formatTime,
    formatFilterDate,
    isSameDay,
  };
};
